import React, { useEffect } from "react";
import "./App.css";
import "./styles/LuxuryDesignSystem.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import NavigationBilingual from "./components/NavigationBilingual";
import FooterBilingual from "./components/FooterBilingual";
import LanguagePrompt from "./components/LanguagePrompt";
import ScrollToTop from "./components/ScrollToTop";

// Bilingual Pages
import HomeBilingual from "./pages/HomeBilingual";
import HouseBilingual from "./pages/HouseBilingual";
import ExpertiseBilingual from "./pages/ExpertiseBilingual";
import WorksBilingual from "./pages/WorksBilingual";
import FounderBilingual from "./pages/FounderBilingual";
import ContactBilingual from "./pages/ContactBilingual";
import AdminSubscribers from "./pages/AdminSubscribers";
import ManageBooking from "./pages/ManageBooking";

import { Toaster } from "./components/ui/sonner";

// Wrapper component that uses LanguageProvider inside Router
const AppContent = () => {
  useEffect(() => {
    // Attempt to remove Emergent badge
    const removeBadge = () => {
      const badges = document.querySelectorAll('[id*="emergent"], [class*="emergent"], [data-emergent]');
      badges.forEach(badge => {
        if (badge && badge.parentNode) {
          badge.parentNode.removeChild(badge);
        }
      });
    };

    // Run immediately and periodically
    removeBadge();
    const interval = setInterval(removeBadge, 1000);

    // Also observe for dynamically added elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(() => {
        removeBadge();
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, []);

  const location = useLocation();
  if (location.pathname.startsWith('/rendez-vous/')) {
    return (
      <>
        <ManageBooking token={location.pathname.split('/')[2] || ''} />
        <Toaster position="top-center" />
      </>
    );
  }
  if (location.pathname.startsWith('/admin')) {
    return (
      <>
        <AdminSubscribers />
        <Toaster />
      </>
    );
  }

  return (
    <LanguageProvider>
      <ScrollToTop />
      <NavigationBilingual />
      <main>
        <Routes>
          {/* Root redirect to /en */}
          <Route path="/" element={<Navigate to="/en" replace />} />
          
          {/* English Routes */}
          <Route path="/en" element={<HomeBilingual />} />
          <Route path="/en/house-of-fenicis" element={<HouseBilingual />} />
          <Route path="/en/expertise" element={<ExpertiseBilingual />} />
          <Route path="/en/selected-works" element={<WorksBilingual />} />
          <Route path="/en/founder" element={<FounderBilingual />} />
          <Route path="/en/contact" element={<ContactBilingual />} />
          
          {/* French Routes */}
          <Route path="/fr" element={<HomeBilingual />} />
          <Route path="/fr/la-maison" element={<HouseBilingual />} />
          <Route path="/fr/expertises" element={<ExpertiseBilingual />} />
          <Route path="/fr/references" element={<WorksBilingual />} />
          <Route path="/fr/fondatrice" element={<FounderBilingual />} />
          <Route path="/fr/contact" element={<ContactBilingual />} />
          
          {/* Fallback - redirect old routes to English versions */}
          <Route path="/house-of-fenicis" element={<Navigate to="/en/house-of-fenicis" replace />} />
          <Route path="/expertise" element={<Navigate to="/en/expertise" replace />} />
          <Route path="/selected-works" element={<Navigate to="/en/selected-works" replace />} />
          <Route path="/founder" element={<Navigate to="/en/founder" replace />} />
          <Route path="/contact" element={<Navigate to="/en/contact" replace />} />
          <Route path="/services" element={<Navigate to="/en/expertise" replace />} />
          <Route path="/engagements" element={<Navigate to="/en/selected-works" replace />} />
          
          {/* Catch all - redirect to English home */}
          <Route path="*" element={<Navigate to="/en" replace />} />
        </Routes>
      </main>
      <FooterBilingual />
      <LanguagePrompt />
      <Toaster position="top-center" />
    </LanguageProvider>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </div>
  );
}

export default App;
